import { useSyncExternalStore } from 'react';

// Theme preference: follow the OS, or force light/dark. Stored per browser;
// index.html applies it before first paint so there's no light flash.
export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'theme';
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
const listeners = new Set<() => void>();

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function apply(pref: ThemePref) {
  const dark = pref === 'dark' || (pref === 'system' && !!media?.matches);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

let current: ThemePref = readPref();
media?.addEventListener('change', () => { if (current === 'system') { apply(current); listeners.forEach((l) => l()); } });

export function setThemePref(pref: ThemePref) {
  current = pref;
  try {
    if (pref === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch { /* storage blocked — still applies for this session */ }
  apply(pref);
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** [preference, whether dark is in effect] */
export function useTheme(): [ThemePref, boolean] {
  const pref = useSyncExternalStore(subscribe, () => current);
  const dark = useSyncExternalStore(subscribe, () => document.documentElement.classList.contains('dark'));
  return [pref, dark];
}
